import { useMemo } from 'react'
import { useConfirmDialog, useError } from '@ztechapps/common.hook'
import { ProjectStatusEnum } from '@ztechapps/common.contract'
import { ProjectStatus } from '../constants/ProjectStatus'
import type { ApolloError } from '@apollo/client/errors'
import map from 'lodash/map'
import { useProjectDocument } from './useProjectDocument'
import { useProjectStateTransition } from './useProjectStateTransition'

type OnUpdate = (value: ProjectStatusEnum) => Promise<void>

export type UseUpdateProjectStatusProps = {
  onUpdate: OnUpdate
  statusMenu: { value: string }[]
  loading: boolean
  error: ApolloError
}

export const useUpdateProjectStatus = (): UseUpdateProjectStatusProps => {
  const { document } = useProjectDocument()
  const [{ changeStatus, moveToCancel }, { loading, error }] = useProjectStateTransition()

  useError(error)

  const onCancel = () => {
    return moveToCancel(document?._id)
  }

  const { onConfirm: onCancelWithConfirmation } = useConfirmDialog({
    text: `This will cancel the project and do the following!
    
     1. Project will be locked for editing and will be archived.
     2. Any task status updates will no longer affect this project.`,
    title: 'Cancel project?',
    buttons: { confirm: { text: 'Yes, cancel it!' } },
    confirmed: onCancel,
  })

  const onUpdate: OnUpdate = async (value) => {
    // If there is no change, do nothing
    if (value === document?.status) {
      return
    }

    // Branch here for the selected status
    switch (value) {
      case ProjectStatusEnum.Cancelled:
        await onCancelWithConfirmation()
        break
      default:
        await changeStatus(value, document?._id)
        break
    }
  }

  const statusMenu = useMemo<{ value: string }[]>(() => {
    return map(ProjectStatus, (e) => ({ value: e.value }))
  }, [])

  return { onUpdate, statusMenu, loading, error }
}
